import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetProductQuery } from '../../Store/api/apiSlice';
import { buildBreadcrumbTrail } from '../../utils/breadcrumbTrail';
import {
  getCategorySlugFromPath,
  getProductSlugFromPath,
  resolveCategoryId,
} from '../../utils/urls';
import { buildBreadcrumbJsonLd, removeJsonLd, upsertJsonLd } from '../../utils/jsonLd';
import styles from '../../styles/Breadcrumbs.module.scss';

const HomeIcon = () => (
  <svg className={styles.homeIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const Breadcrumbs = () => {
  const location = useLocation();
  const { list: categories, isLoading: categoriesLoading } = useSelector((state) => state.categories);
  const pageTrail = useSelector((state) => state.breadcrumb.items);

  const categoryMatch = useMatch('/categories/:slugOrId');
  const productMatch = useMatch('/products/:slugOrId');
  const isCategoryPage = Boolean(categoryMatch);
  const isProductPage = Boolean(productMatch);
  const categorySlugOrId = categoryMatch?.params?.slugOrId
    || (isCategoryPage ? getCategorySlugFromPath(location.pathname) : '');
  const productSlugOrId = productMatch?.params?.slugOrId
    || (isProductPage ? getProductSlugFromPath(location.pathname) : '');
  const categoryId = resolveCategoryId(categories, categorySlugOrId);

  const { data: product, isLoading: productLoading, isFetching: productFetching } = useGetProductQuery(
    { id: productSlugOrId },
    { skip: !isProductPage || !productSlugOrId }
  );

  const items = useMemo(
    () =>
      buildBreadcrumbTrail({
        pathname: location.pathname,
        search: location.search,
        categories,
        categoryId,
        slugOrId: isCategoryPage ? categorySlugOrId : productSlugOrId,
        product,
        productLoading: productLoading || productFetching,
        categoriesLoading,
        pageTrail,
      }),
    [
      categories,
      categoriesLoading,
      categoryId,
      categorySlugOrId,
      isCategoryPage,
      location.pathname,
      location.search,
      pageTrail,
      product,
      productFetching,
      productLoading,
      productSlugOrId,
    ]
  );

  useEffect(() => {
    if (!items?.length) {
      removeJsonLd('seo-breadcrumbs');
      return undefined;
    }

    upsertJsonLd('seo-breadcrumbs', buildBreadcrumbJsonLd(items));

    return () => removeJsonLd('seo-breadcrumbs');
  }, [items]);

  if (!items?.length) {
    return null;
  }

  return (
    <div className={styles.breadcrumbsBar}>
      <nav className={styles.breadcrumbs} aria-label="Навігаційний ланцюжок">
        <ol className={styles.list}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isHome = item.path === '/' && index === 0;
            const showLink = Boolean(item.path) && !isLast;

            return (
              <li key={`${item.label}-${index}`} className={styles.item}>
                {index > 0 && (
                  <span className={styles.separator} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
                {showLink ? (
                  <Link className={styles.link} to={item.path}>
                    {isHome ? (
                      <>
                        <HomeIcon />
                        <span>{item.label}</span>
                      </>
                    ) : (
                      item.label
                    )}
                  </Link>
                ) : (
                  <span
                    className={isLast ? styles.current : styles.text}
                    aria-current={isLast ? 'page' : undefined}
                    title={item.label}
                  >
                    {isHome && <HomeIcon />}
                    <span>{item.label}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
